import type { MenuFavoritos } from '@construtora/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { STALE_TIME } from '@/app/query-client';
import { api } from '@/shared/lib/api';

/**
 * As telas favoritas do menu (`.spec/015`, decisão 75).
 *
 * Em `shared/api`, e não numa feature: quem lê é o `AppShell`, que não é de
 * feature nenhuma, e a rota vale para qualquer usuário autenticado.
 *
 * Nada aqui recebe id — a API opera sobre o portador do token.
 */

export const chavesMenuFavoritos = { meus: ['preferencias', 'menu-favoritos'] as const };

const CAMINHO = '/api/v1/preferencias/menu-favoritos';

export function useMenuFavoritos(): UseQueryResult<MenuFavoritos, Error> {
  return useQuery({
    queryKey: chavesMenuFavoritos.meus,
    queryFn: () => api.get<MenuFavoritos>(CAMINHO),
    // Só esta pessoa escreve aqui, e escreve por esta tela — que atualiza o
    // cache na hora. Revalidar a cada meio minuto seria pergunta sem resposta
    // nova; o `cadastro` cobre o caso da outra aba.
    staleTime: STALE_TIME.cadastro,
  });
}

/**
 * Grava a lista inteira, com atualização **otimista**.
 *
 * Otimista porque a estrela e a seta são gestos de meio segundo: esperar a
 * resposta para a tela mudar faria o clique parecer perdido, e o segundo
 * clique, dado por impaciência, desfaria o primeiro. Na falha o cache volta ao
 * que era e a consulta se refaz — o menu mostra o que ficou no servidor.
 *
 * **Em fila** (`scope`): três cliques rápidos na seta são três `PUT`, e sem a
 * fila o segundo podia chegar depois do terceiro e gravar a ordem do meio. Com
 * ela, cada um espera o anterior, e o último gesto é o que fica.
 */
export function useGravarMenuFavoritos(): UseMutationResult<
  MenuFavoritos,
  Error,
  MenuFavoritos,
  { anterior: MenuFavoritos | undefined }
> {
  const cliente = useQueryClient();

  return useMutation({
    scope: { id: 'menu-favoritos' },
    mutationFn: (dados: MenuFavoritos) => api.put<MenuFavoritos>(CAMINHO, dados),
    /*
      O cache muda **antes** do primeiro `await`: o `onMutate` roda síncrono no
      `mutate()`, e é isso que deixa o segundo clique ler a lista que o primeiro
      acabou de escrever. Cancelar a consulta em voo vem depois, e cumpre o
      mesmo papel — uma leitura antiga chegando não sobrescreve o gesto.
      `revert: false` porque o padrão devolveria o cache ao estado do início
      da leitura, e esse estado é anterior ao gesto que acabou de entrar.
    */
    onMutate: async (dados) => {
      const anterior = cliente.getQueryData<MenuFavoritos>(chavesMenuFavoritos.meus);
      cliente.setQueryData(chavesMenuFavoritos.meus, dados);
      await cliente.cancelQueries({ queryKey: chavesMenuFavoritos.meus }, { revert: false });
      return { anterior };
    },
    /*
      Na falha, volta e pergunta ao servidor. No sucesso, **nada**: a resposta
      de um `PUT` antigo, aplicada ao cache, apagaria o gesto seguinte que ainda
      está na fila — e o cache já tem o que foi gravado.
    */
    onError: async (_erro, _dados, contexto) => {
      cliente.setQueryData(chavesMenuFavoritos.meus, contexto?.anterior);
      await cliente.invalidateQueries({ queryKey: chavesMenuFavoritos.meus });
    },
  });
}
